import { SIGNED_DISPLAY_URL_TTL_SECONDS, SIGNED_DOCUMENT_URL_REFRESH_SAFETY_WINDOW_SECONDS } from '@repo/common';
const DISPLAY_URL_REFRESH_SAFETY_WINDOW_MS = SIGNED_DOCUMENT_URL_REFRESH_SAFETY_WINDOW_SECONDS * 1000;
/**
 * Default refetch cadence for every watched query, sized to the hour-long display URL lifetime
 * (pictures, thumbnails) so those are replaced well before they expire.
 *
 * Protected document URLs live for only 60 seconds; they are refreshed by evicting each
 * `Resource.url` just before its own expiry (see `signedDisplayUrlTypePolicies`), which
 * refetches only the queries that hold that document instead of polling every query.
 */
export const SIGNED_DISPLAY_URL_POLL_INTERVAL_MS = (SIGNED_DISPLAY_URL_TTL_SECONDS / 2) * 1000;
function parseAwsSigningDate(value) {
    const match = /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/u.exec(value);
    if (!match)
        return undefined;
    const [, year, month, day, hour, minute, second] = match;
    if ([year, month, day, hour, minute, second].some(part => part === undefined))
        return undefined;
    return Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
}
function getSignedDisplayUrlExpiryMs(value) {
    try {
        const parsed = new URL(value, 'https://starthub.invalid');
        const localExpiry = /^\/api\/storage\/local\/(\d+)\//u.exec(parsed.pathname)?.[1];
        if (localExpiry !== undefined)
            return Number(localExpiry) * 1000;
        const cloudFrontExpiry = parsed.searchParams.get('Expires');
        if (cloudFrontExpiry !== null && /^\d+$/u.test(cloudFrontExpiry))
            return Number(cloudFrontExpiry) * 1000;
        const signingDate = parsed.searchParams.get('X-Amz-Date');
        const lifetimeSeconds = parsed.searchParams.get('X-Amz-Expires');
        if (signingDate === null || lifetimeSeconds === null || !/^\d+$/u.test(lifetimeSeconds))
            return undefined;
        const signedAtMs = parseAwsSigningDate(signingDate);
        return signedAtMs === undefined ? undefined : signedAtMs + Number(lifetimeSeconds) * 1000;
    }
    catch {
        return undefined;
    }
}
export function readFreshDisplayUrl(existing, nowMs = Date.now()) {
    if (existing === undefined || existing === null)
        return existing;
    const expiresAtMs = getSignedDisplayUrlExpiryMs(existing);
    return expiresAtMs !== undefined && expiresAtMs <= nowMs + DISPLAY_URL_REFRESH_SAFETY_WINDOW_MS
        ? undefined
        : existing;
}
const expiringDisplayUrlFieldPolicy = {
    read(existing) {
        return readFreshDisplayUrl(existing);
    },
};
const resourceUrlRefreshTimers = new WeakMap();
/**
 * Evicts a cached `Resource.url` just before its signature expires. Watched queries holding it
 * become incomplete and refetch it, so a short-lived document link is never served stale.
 */
export function scheduleResourceUrlRefresh(cache, entityId, url, nowMs = Date.now()) {
    let timers = resourceUrlRefreshTimers.get(cache);
    if (!timers) {
        timers = new Map();
        resourceUrlRefreshTimers.set(cache, timers);
    }
    const pending = timers.get(entityId);
    if (pending !== undefined)
        clearTimeout(pending);
    timers.delete(entityId);
    const expiresAtMs = typeof url === 'string' ? getSignedDisplayUrlExpiryMs(url) : undefined;
    if (expiresAtMs === undefined)
        return;
    const delayMs = Math.max(expiresAtMs - DISPLAY_URL_REFRESH_SAFETY_WINDOW_MS - nowMs, 0);
    const activeTimers = timers;
    activeTimers.set(entityId, setTimeout(() => {
        activeTimers.delete(entityId);
        cache.evict({ id: entityId, fieldName: 'url' });
    }, delayMs));
}
/** Field merge functions cannot see the entity id, so the Resource type merge schedules the refresh. */
const resourceTypePolicy = {
    merge(existing, incoming, { cache, mergeObjects, readField }) {
        const id = readField('id', incoming);
        const entityId = id === undefined ? undefined : cache.identify({ __typename: 'Resource', id });
        if (entityId && readField('url', incoming) !== undefined) {
            scheduleResourceUrlRefresh(cache, entityId, readField('url', incoming));
        }
        return mergeObjects(existing, incoming);
    },
    fields: {
        url: expiringDisplayUrlFieldPolicy,
    },
};
export const signedDisplayUrlTypePolicies = {
    User: {
        fields: {
            picture: {
                merge: true,
            },
        },
    },
    Resource: resourceTypePolicy,
    Picture: {
        fields: {
            originalUrl: expiringDisplayUrlFieldPolicy,
            thumbnailUrl: expiringDisplayUrlFieldPolicy,
        },
    },
};
/**
 * Default empty response for paginated queries.
 * Used as fallback when no cached data exists yet.
 */
export const defaultPaginatedResponse = {
    __typename: 'Connection',
    totalCount: 0,
    edges: [],
    pageInfo: {
        __typename: 'PageInfo',
        hasNextPage: false,
        hasPreviousPage: false,
        startCursor: undefined,
        endCursor: undefined,
    },
};
/**
 * Generic pagination merge policy for Apollo Client cache.
 * Implements "load more" / infinite scroll by appending new edges to existing ones.
 *
 * @param keyArgs - REQUIRED. Determines cache key strategy:
 *   - Array with nested fields: Separate cache per unique filter combination
 *   - Example: ['input', ['status', 'categoryId']]
 *
 * IMPORTANT: keyArgs is mandatory to prevent cache collisions between queries
 * with different filter parameters.
 *
 * @example
 * ```ts
 * const typePolicies = {
 *   Query: {
 *     fields: {
 *       courses: createPaginationPolicy(['input', ['status', 'categoryId']]),
 *       discountDeals: createPaginationPolicy(['input', ['workflowStatus']]),
 *     }
 *   }
 * };
 * ```
 */
const cursorAwareKeyArgs = args => {
    // Dynamic key generation strategy:
    // Use all arguments as the cache key, EXCEPT for pagination cursors.
    // This ensures 100% uniqueness for every permutation of filters/limits.
    // Keys are sorted to ensure stability (e.g. {a:1, b:2} == {b:2, a:1})
    if (!args)
        return false;
    // Helper to clean and sort object keys recursively
    const cleanAndSort = (obj) => {
        if (!obj || typeof obj !== 'object')
            return obj;
        if (Array.isArray(obj)) {
            return obj.map(cleanAndSort);
        }
        // Get keys, sort them to ensure stability
        const keys = Object.keys(obj);
        const sortedKeys = keys.sort();
        const newObj = {};
        for (const key of sortedKeys) {
            // Skip pagination cursors
            if (key === 'after' || key === 'before')
                continue;
            // Recursively clean and sort
            newObj[key] = cleanAndSort(obj[key]);
        }
        return newObj;
    };
    // Return a stable string key
    return JSON.stringify(cleanAndSort(args));
};
/**
 * Like {@link cursorAwareKeyArgs}, but also ignores `first` / nested `input.first`
 * so a live queue can grow its poll window and still share one connection cache
 * with fetchMore pages.
 */
const cursorAndPageSizeAwareKeyArgs = args => {
    if (!args)
        return false;
    const cleanAndSort = (obj) => {
        if (!obj || typeof obj !== 'object')
            return obj;
        if (Array.isArray(obj)) {
            return obj.map(cleanAndSort);
        }
        const keys = Object.keys(obj);
        const sortedKeys = keys.sort();
        const newObj = {};
        for (const key of sortedKeys) {
            if (key === 'after' || key === 'before' || key === 'first')
                continue;
            newObj[key] = cleanAndSort(obj[key]);
        }
        return newObj;
    };
    return JSON.stringify(cleanAndSort(args));
};
/**
 * Cursor-aware cache keying for connections whose pages are merged by
 * the CONSUMER (useInfiniteList's fetchMore.updateQuery). Apollo runs a
 * field policy's merge on the updateQuery result as well, so pairing
 * that hook with `createPaginationPolicy` appends every page's already
 * concatenated edges again on each write. The incoming value here is
 * always a complete list (first page, refetch, or the updateQuery
 * concat), so replacement is the single merge path.
 */
export function createUpdateQueryPaginationPolicy(keyArgs) {
    return {
        keyArgs: keyArgs ?? cursorAwareKeyArgs,
        merge(_existing = defaultPaginatedResponse, incoming = defaultPaginatedResponse) {
            return incoming;
        },
    };
}
export function createPaginationPolicy(keyArgs) {
    return {
        keyArgs: keyArgs ?? cursorAwareKeyArgs,
        merge(existing = defaultPaginatedResponse, incoming = defaultPaginatedResponse, { args }) {
            // Fresh query (no cursor) - replace cache entirely
            // We check both top-level and nested input 'after' to be safe
            const isFirstPage = !(args?.after || args?.input?.after);
            if (isFirstPage) {
                return incoming;
            }
            // FetchMore with cursor - append new edges to existing
            return {
                ...incoming,
                edges: [...existing.edges, ...incoming.edges],
                totalCount: incoming.totalCount ?? existing.totalCount,
            };
        },
    };
}
/**
 * Cursor pagination for a newest-first list that is also polled.
 *
 * Cursor pages append normally. A cursorless poll replaces the refreshed head,
 * then keeps the previously loaded tail after the last overlapping item. This
 * prevents polling the first page from discarding pages the user loaded earlier.
 */
export function createPollingPreservingPaginationPolicy(keyArgs) {
    return {
        keyArgs: keyArgs ?? cursorAwareKeyArgs,
        merge(existing = defaultPaginatedResponse, incoming = defaultPaginatedResponse, { args, readField }) {
            const asStoreObjectOrRef = (value) => {
                if (value == null || typeof value !== 'object' || Array.isArray(value))
                    return undefined;
                return value;
            };
            const edgeId = (edge) => {
                const edgeObject = asStoreObjectOrRef(edge);
                if (!edgeObject)
                    return undefined;
                const nodeObject = asStoreObjectOrRef(readField('node', edgeObject));
                if (!nodeObject)
                    return undefined;
                const id = readField('id', nodeObject);
                return id == null ? undefined : String(id);
            };
            const appendUnique = (base, additions) => {
                const knownIds = new Set(base.map(edgeId).filter((id) => id !== undefined));
                return [
                    ...base,
                    ...additions.filter(edge => {
                        const id = edgeId(edge);
                        return id === undefined || !knownIds.has(id);
                    }),
                ];
            };
            const hasCursor = Boolean(args?.after || args?.input?.after);
            if (hasCursor) {
                return {
                    ...incoming,
                    edges: appendUnique(existing.edges, incoming.edges),
                    totalCount: incoming.totalCount ?? existing.totalCount,
                };
            }
            if (incoming.edges.length === 0) {
                return incoming;
            }
            const lastIncomingId = edgeId(incoming.edges.at(-1));
            const overlapIndex = lastIncomingId === undefined
                ? -1
                : existing.edges.findIndex((edge) => edgeId(edge) === lastIncomingId);
            const retainedTail = overlapIndex >= 0 ? existing.edges.slice(overlapIndex + 1) : existing.edges;
            const edges = appendUnique(incoming.edges, retainedTail);
            const retainedLoadedPage = edges.length > incoming.edges.length;
            return {
                ...incoming,
                edges,
                pageInfo: retainedLoadedPage ? existing.pageInfo : incoming.pageInfo,
                totalCount: incoming.totalCount ?? existing.totalCount,
            };
        },
    };
}
/**
 * Pagination merge for newest-first live queues (e.g. admin pending reports).
 *
 * - `fetchMore` (has `after`): append like {@link createPaginationPolicy}, skipping
 *   edges whose node id is already present (guards duplicate pages from concurrent fetches).
 * - Cursorless writes (poll / refetch / local updateQuery): full replace.
 *
 * Consumers must poll/refetch with `first` capped by the server max and refill extra
 * pages via fetchMore under a shared busy guard with Load more.
 */
export function createNewestFirstPollingPaginationPolicy(keyArgs) {
    return {
        keyArgs: keyArgs ?? cursorAndPageSizeAwareKeyArgs,
        merge(existing = defaultPaginatedResponse, incoming = defaultPaginatedResponse, { args, readField }) {
            const isFirstPage = !(args?.after || args?.input?.after);
            if (!isFirstPage) {
                const asStoreObjectOrRef = (value) => {
                    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
                        return undefined;
                    }
                    return value;
                };
                const edgeId = (edge) => {
                    const edgeObject = asStoreObjectOrRef(edge);
                    if (!edgeObject)
                        return undefined;
                    const nodeObject = asStoreObjectOrRef(readField('node', edgeObject));
                    if (!nodeObject)
                        return undefined;
                    const id = readField('id', nodeObject);
                    return id == null ? undefined : String(id);
                };
                const existingIds = new Set(existing.edges
                    .map((edge) => edgeId(edge))
                    .filter((id) => id !== undefined));
                const appended = incoming.edges.filter((edge) => {
                    const id = edgeId(edge);
                    return id === undefined || !existingIds.has(id);
                });
                return {
                    ...incoming,
                    edges: [...existing.edges, ...appended],
                    totalCount: incoming.totalCount ?? existing.totalCount,
                };
            }
            return incoming;
        },
    };
}
//# sourceMappingURL=apollo-helpers.js.map